import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { UserPreferenceContext } from '@contexts/user-preference-context';
import { Page } from '@models/page';
import {
  SalesforceConnection,
  SalesforceConnectionEdition,
} from '@modules/salesforce/models';
import { SalesforceConnectionsService } from '@modules/salesforce/services';

/**
 * Specifies sorting directions for connection lists to be used with the SalesforceConnectionsService.findAll API.
 */
type SORT_DIRECTION = 'asc' | 'desc';

interface FetchConnectionsOptions {
  searchValue?: string;
  queryString?: string;
  defaultSortKey?: string;
  defaultSortDirection?: SORT_DIRECTION;
  defaultSize?: number;
}

const getFilterQuery = (
  applicationVersionIdParam: string,
  queryString?: string,
): string => {
  let query = `${applicationVersionIdParam}`;
  if (queryString) {
    query += queryString;
  }
  return query;
};

const setConnectionEdition = (connections: SalesforceConnection[]) => {
  connections.forEach((connection) =>
    connection.isSandbox
      ? (connection.salesforceConnectionEdition =
          SalesforceConnectionEdition.SANDBOX)
        : SalesforceConnectionEdition.PRODUCTION_OR_DEVELOPER,
  );
};

const useFetchConnections = (options?: FetchConnectionsOptions) => {
  const {
    searchValue,
    queryString,
    defaultSortKey,
    defaultSortDirection,
    defaultSize,
  } = options || {};

  const [connectionsList, setConnectionList] = useState<SalesforceConnection[]>(
    [],
  );
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(defaultSize || 20);
  const [sortKey, setSortKey] = useState(defaultSortKey);
  const [sortDirection, setSortDirection] = useState<SORT_DIRECTION>(
    defaultSortDirection || 'asc',
  );

  const { versionId, applicationId } =
    useContext(UserPreferenceContext)?.userPreference?.preference
      ?.projectPreference || {};

  const applicationVersionIdParam = useMemo(
    () => `applicationVersionId:${versionId}`,
    [versionId],
  );

  // Function to handle load more
  const handleLoadMore = useCallback(() => {
    setPage((prevPage) => prevPage + 1);
  }, []);

  /**
   * Fetches connections from the backend and updates the state.
   * @param isAppendToList - Whether to append the fetched connections to the existing list.
   */
  const loadConnections = useCallback(
    (isAppendToList = false) => {
      setIsLoading(true);
      const query = getFilterQuery(queryString);
      const sort = sortKey ? `${sortKey},${sortDirection}` : '';
      SalesforceConnectionsService.getVersionSpecificConnection(versionId, applicationId, query).then(
        (data: Page<SalesforceConnection>) => {
          const connections = data.content;
          if (connections?.length) setConnectionEdition(connections);
          setConnectionList((prevConnections) => {
            if (isAppendToList) {
              return [...prevConnections, ...connections];
            } else {
              return connections;
            }
          });
          setIsLoading(false);
        },
      );
    },
    [
      applicationVersionIdParam,
      queryString,
      sortKey,
      sortDirection,
      size,
      page,
    ],
  );

  // Refresh method to reload connections
  const refreshPage = useCallback(() => {
    loadConnections();
  }, [loadConnections]);

  // Refresh list data by resetting page and size
  const refreshListData = useCallback(() => {
    setPage(0);
    setSize(20);
    loadConnections();
  }, [loadConnections]);

  // Load connections on component mount and when dependencies change
  useEffect(() => {
    loadConnections();
  }, [loadConnections, searchValue, queryString, sortKey, sortDirection, page]);

  return {
    connectionsList,
    isLoading,
    hasMore,
    handleLoadMore,
    sortKey,
    sortDirection,
    setSortKey,
    setSortDirection,
    refreshPage,
    refreshListData,
  };
};

export { type SORT_DIRECTION, useFetchConnections };
